import React from 'react'
import { cn } from '@/utils/cn'
import { formatCurrency } from '@/utils/formatters'
import { FiInfo } from 'react-icons/fi'

interface CommissionBreakdownProps {
    grossAmount: number
    platformCommission: number
    buyerProtectionFee: number
    netAmount?: number
    currency?: string
    showDetails?: boolean
    compact?: boolean
    className?: string
}

export const CommissionBreakdown: React.FC<CommissionBreakdownProps> = ({
    grossAmount,
    platformCommission,
    buyerProtectionFee,
    netAmount,
    currency = 'GHS',
    showDetails = true,
    compact = false,
    className
}) => {
    const platformFee = (grossAmount * platformCommission)
    const protectionFee = (grossAmount * buyerProtectionFee)
    const calculatedNetAmount = netAmount || (grossAmount - platformFee - protectionFee)
    const totalDeduction = platformFee + protectionFee

    if (compact) {
        return (
            <div className={cn('text-sm', className)}>
                <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Gross:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                        {formatCurrency(grossAmount, currency)}
                    </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>Fees ({(platformCommission * 100).toFixed(0)}% + {(buyerProtectionFee * 100).toFixed(0)}%):</span>
                    <span className="text-red-600 dark:text-red-400">-{formatCurrency(totalDeduction, currency)}</span>
                </div>
                <div className="flex items-center justify-between font-medium text-gray-900 dark:text-white mt-2 pt-1 border-t border-gray-200 dark:border-gray-700">
                    <span>You Get:</span>
                    <span className="text-green-600 dark:text-green-400">{formatCurrency(calculatedNetAmount, currency)}</span>
                </div>
            </div>
        )
    }

    return (
        <div className={cn('rounded-2xl border border-slate-200 bg-white dark:border-gray-800 dark:bg-gray-900', className)}>
            <div className="p-3 sm:p-4">
                {/* Header */}
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                    <FiInfo className="w-5 h-5 text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Commission Breakdown
                    </h3>
                </div>

                {/* Breakdown */}
                <div className="space-y-3">
                    {/* Gross Amount */}
                    <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-700">
                        <span className="text-gray-600 dark:text-gray-400">Gross Amount</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(grossAmount, currency)}
                        </span>
                    </div>

                    {/* Platform Commission */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Platform Commission</span>
                            <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                                {(platformCommission * 100).toFixed(0)}%
                            </span>
                        </div>
                        <span className="text-red-600 dark:text-red-400 font-medium">
                            -{formatCurrency(platformFee, currency)}
                        </span>
                    </div>

                    {/* Buyer Protection Fee */}
                    {buyerProtectionFee > 0 && (
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Buyer Protection</span>
                                <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2 py-0.5 rounded-full">
                                    {(buyerProtectionFee * 100).toFixed(0)}%
                                </span>
                            </div>
                            <span className="text-red-600 dark:text-red-400 font-medium">
                                -{formatCurrency(protectionFee, currency)}
                            </span>
                        </div>
                    )}

                    {/* Total Deduction */}
                    {showDetails && (
                        <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 -mx-4 px-4 py-3">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Fees</span>
                            <span className="font-semibold text-red-600 dark:text-red-400">
                                -{formatCurrency(totalDeduction, currency)}
                            </span>
                        </div>
                    )}
                </div>

                {/* Final Amount */}
                <div className="mt-3 rounded-xl border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20 sm:mt-4">
                    <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900 dark:text-white">You Receive</span>
                        <span className="text-base font-bold text-green-600 dark:text-green-400">
                            {formatCurrency(calculatedNetAmount, currency)}
                        </span>
                    </div>
                </div>

                {/* Info Text */}
                {showDetails && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                        Commission rates vary by subscription tier. Upgrade your plan to reduce fees.
                    </p>
                )}
            </div>
        </div>
    )
}